import { useNavigate } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import {
  Delete,
  Download,
  LayersFront,
  Link,
  Pencil,
  Replace,
  Suitcase,
} from '@factorialco/f0-react/icons/app'
import {
  QuoteDetailDocument,
  QuoteDetailQuery,
  QuotesListTableDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import { quoteStatusAsVariant, quoteTranslateStatus } from 'modules/finance/components/Quotes/utils'
import { useChangeStatusQuoteDialog } from 'modules/finance/hooks/quotes/useChangeStatusQuoteDialog'
import { useConvertQuoteIntoInvoice } from 'modules/finance/hooks/quotes/useConvertQuoteIntoInvoice'
import { useCreateProjectFromQuote } from 'modules/finance/hooks/quotes/useCreateProjectFromQuote'
import { useDeleteQuoteDialog } from 'modules/finance/hooks/quotes/useDeleteQuoteDialog'
import { useDownloadQuotePdf } from 'modules/finance/hooks/quotes/useDownloadQuotePdf'
import { useDuplicateQuote } from 'modules/finance/hooks/quotes/useDuplicateQuote'
import { contactFullName } from 'modules/finance/lib/contactsUtils'

interface Props {
  quote?: QuoteDetailQuery['finance']['quotes'][0]
  canSeeProjects?: boolean
}

export const QuoteDetailHeader = ({ quote, canSeeProjects = false }: Props) => {
  const {
    access: { locale },
  } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const convertQuoteIntoInvoice = useConvertQuoteIntoInvoice()
  const createProjectFromQuote = useCreateProjectFromQuote({
    refetchQueries: [QuotesListTableDocument, QuoteDetailDocument],
  })
  const downloadQuotePdf = useDownloadQuotePdf()
  const duplicateQuote = useDuplicateQuote({ refetchQueries: [QuotesListTableDocument] })
  const { showDeleteQuoteDialog } = useDeleteQuoteDialog()
  const { showChangeStatusQuoteDialog, modal: changeStatusModal } = useChangeStatusQuoteDialog({
    refetchQueries: [QuoteDetailDocument],
  })

  if (!quote) {
    return null
  }

  const amountFormatted =
    quote.totalAmountCents && quote.currency
      ? formattedAmount({
          amountCents: Number(quote.totalAmountCents),
          currency: quote.currency,
          locale,
        })
      : '-'

  const canCreateProject = quote._policies?.convertToProject?.can

  const projectAction = canCreateProject
    ? {
        label: i18n.t('finance.quotes.actions.create_project'),
        icon: Suitcase,
        onClick: () => {
          createProjectFromQuote(quote.id)
        },
      }
    : quote.projectId && canSeeProjects
    ? {
        label: i18n.t('finance.quotes.actions.go_to_project'),
        icon: Link,
        onClick: () => {
          navigate(
            navigation.projects.id.details.overview.fullPath({
              id: String(quote.projectId),
            })
          )
        },
      }
    : undefined

  const invoiceAction =
    quote.status !== 'rejected' &&
    !quote.financialDocumentAssociation?.financialDocument &&
    quote._policies?.convertToInvoice?.can
      ? {
          label: i18n.t('finance.quotes.actions.convert_to_invoice'),
          icon: Replace,
          onClick: () => {
            convertQuoteIntoInvoice(quote.id)
          },
        }
      : quote.financialDocumentAssociation?.financialDocument
      ? {
          label: i18n.t('finance.quotes.actions.go_to_invoice'),
          icon: Link,
          onClick: () => {
            navigate(
              navigation.finance.sales.salesInvoices.detail.fullPath({
                invoiceId: Number(quote.financialDocumentAssociation?.financialDocument?.id),
              })
            )
          },
        }
      : undefined

  const primaryAction = !!projectAction
    ? projectAction
    : !quote.projectId
    ? invoiceAction
    : undefined

  const secondaryActions = [
    quote.status === 'draft' && {
      label: i18n.t('finance.quotes.actions.edit'),
      icon: Pencil,
      onClick: () => {
        navigate(navigation.finance.sales.quotes.quoteId.fullPath({ quoteId: quote.id }))
      },
    },
    (canCreateProject || !!quote.projectId) && invoiceAction,
  ].filter(Boolean)

  const otherActions = [
    {
      label: i18n.t('finance.quotes.actions.download_pdf'),
      icon: Download,
      onClick: () => {
        downloadQuotePdf(quote.id)
      },
    },
    {
      label: i18n.t('finance.quotes.actions.duplicate'),
      icon: LayersFront,
      onClick: () => {
        duplicateQuote(quote.id)
      },
    },
    (quote.status === 'draft' || quote.status === 'rejected') && {
      label: i18n.t('finance.quotes.actions.delete'),
      icon: Delete,
      critical: true,
      onClick: () => {
        showDeleteQuoteDialog({
          quoteId: quote.id,
          callback: () => {
            navigate(navigation.finance.sales.quotes.fullPath())
          },
        })
      },
    },
  ].filter(Boolean)

  return (
    <>
      <ResourceHeader
        title={quote.quoteNumber || '-'}
        description={i18n.t('finance.quotes.columns.quote')}
        status={{
          label: i18n.t('finance.sale_invoices.detail.header.status'),
          text: quoteTranslateStatus(quote.status),
          variant: quoteStatusAsVariant(quote.status),
          actions: quote.financialDocumentAssociation?.financialDocument
            ? undefined
            : [
                {
                  label: 'Edit',
                  icon: Pencil,
                  onClick: () => {
                    showChangeStatusQuoteDialog({ quote: quote })
                  },
                },
              ],
        }}
        metadata={[
          {
            label: i18n.t('finance.sale_invoices.detail.header.billed_to'),
            value: {
              type: 'text',
              content: contactFullName(quote.client),
            },
          },
          {
            label: i18n.t('finance.sale_invoices.detail.header.amount'),
            value: {
              type: 'text',
              content: amountFormatted,
            },
          },
        ]}
        primaryAction={primaryAction}
        secondaryActions={secondaryActions}
        otherActions={otherActions}
      />
      {changeStatusModal}
    </>
  )
}
